import { main_url } from "../pages/basefile";
import { getValidToken } from "../auth/getToken";

async function getAuthHeaders() {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchProjectDatagridRows({
  page = 0,
  pageSize = 10,
  search = "",
  status = "",
  clientNames = [],
  sortField = "project_id",
  sortOrder = "desc",
}) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    search,
    sortField,
    sortOrder,
  });

  if (Array.isArray(clientNames) && clientNames.length > 0) {
    params.set("clientNames", clientNames.join(","));
  }
  if (status) {
    params.set("status", status);
  }

  const response = await fetch(
    `${main_url}/api/v1/projects/datagrid/rows?${params.toString()}`,
    {
      method: "GET",
      headers: await getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch projects");
  }

  return response.json();
}

export async function generateProjectPid(name, cid) {
  const params = new URLSearchParams({ name, cid });
  const response = await fetch(`${main_url}/api/v1/projects/generate-pid?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to generate project ID");
  }

  return response.json();
}

export async function createProject(payload) {
  const response = await fetch(`${main_url}/api/v1/projects`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to create project");
  }

  return response.json();
}

export async function fetchProjectById(projectId) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch project");
  }

  return response.json();
}

export async function fetchProjectsDropdownByClient(cid) {
  const params = new URLSearchParams({ cid });
  const response = await fetch(`${main_url}/api/v1/projects/dropdown?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch project dropdown");
  }

  return response.json();
}

export async function updateProject(projectId, payload) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}`, {
    method: "PUT",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update project");
  }

  return response.json();
}

export async function resyncProjectSchedules(projectId) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/schedules/resync`, {
    method: "POST",
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to resync project schedules");
  }
  return response.json();
}

export async function addProjectTimeline(projectId, payload) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/timelines`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to add timeline");
  }

  return response.json();
}

export async function addProjectTimelinesBulk(projectId, payload) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/timelines/bulk`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to add timelines in bulk");
  }

  return response.json();
}

export async function fetchProjectTimelines(projectId) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/timelines`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch project timelines");
  }

  return response.json();
}

export async function updateProjectTimeline(projectId, timelineId, payload) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/timelines/${timelineId}`, {
    method: "PUT",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to update project timeline");
  }

  return response.json();
}

export async function fetchProjectDocuments(projectId) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/documents`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch project documents");
  }

  return response.json();
}

export async function uploadProjectDocument(projectId, { doctype, document_name, file }) {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }

  const formData = new FormData();
  formData.append("doctype", doctype);
  formData.append("document_name", document_name);
  formData.append("file", file);

  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/documents`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to upload project document");
  }

  return response.json();
}

export async function deleteProjectDocument(projectId, documentId) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}/documents/${documentId}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete project document");
  }
}

export function getProjectDocumentDownloadUrl(projectId, documentId) {
  return `${main_url}/api/v1/projects/${projectId}/documents/${documentId}/download`;
}

export async function fetchExecutions({ page = 1, limit = 100, executionStatus = "" } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (executionStatus) params.set("execution_status", executionStatus);

  const response = await fetch(`${main_url}/api/v1/executions?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch executions");
  }

  return response.json();
}

export async function fetchExecutionSchedules(executionId, { page = 1, limit = 100 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  const response = await fetch(
    `${main_url}/api/v1/executions/${executionId}/schedules?${params.toString()}`,
    {
      method: "GET",
      headers: await getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch execution schedules");
  }

  return response.json();
}

export async function addProjectUser(payload) {
  const response = await fetch(`${main_url}/api/v1/project-users`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to add project user");
  }

  return response.json();
}

export async function fetchProjectUsers({ pid, page = 1, limit = 200 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (pid) params.set("pid", pid);

  const response = await fetch(`${main_url}/api/v1/project-users?${params.toString()}`, {
    method: "GET",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to fetch project users");
  }

  return response.json();
}

export async function deleteProjectUser(pid, username) {
  const response = await fetch(`${main_url}/api/v1/project-users/${encodeURIComponent(pid)}/${encodeURIComponent(username)}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete project user");
  }
}

export async function deleteProject(projectId) {
  const response = await fetch(`${main_url}/api/v1/projects/${projectId}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to delete project");
  }
}
